import { Dialog, EmptyState, Spinner, Table } from '@1k5-tech-es/grado-react';
import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';

import { api } from '../../api/client';
import type { ChangeEntry, MonthlyRow, Period } from '../../api/types';
import { ErrorBlock } from '../../components/ui';
import { formatDateTime, periodLabel } from '../../lib/format';
import { describeSource, FIELD_LABELS } from './columns';

export function HistoryDialog({ row, period, onClose }: { row: MonthlyRow; period: Period; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['history', row.advisor.id, period.year, period.month],
    queryFn: () =>
      api
        .get<{ entries: ChangeEntry[] }>(`/api/monthly/${period.year}/${period.month}/advisors/${row.advisor.id}/history`)
        .then((r) => r.entries),
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Historial · ${row.advisor.fullName}`}
      description={periodLabel(period)}
      className="w-[min(44rem,calc(100vw-2rem))]"
    >
      {query.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : query.isError ? (
        <ErrorBlock error={query.error} />
      ) : query.data.length === 0 ? (
        <EmptyState
          compact
          icon={History}
          title="Sin cambios registrados"
          description="Los valores actuales proceden de la carga inicial del Excel histórico."
        />
      ) : (
        <Table compact plain>
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Campo</th>
              <th className="k5-num">Antes</th>
              <th className="k5-num">Después</th>
              <th>Origen</th>
            </tr>
          </thead>
          <tbody>
            {query.data.map((entry) => (
              <tr key={entry.id}>
                <td className="whitespace-nowrap text-muted-foreground">{formatDateTime(entry.createdAt)}</td>
                <td className="font-medium">{FIELD_LABELS[entry.field] ?? entry.field}</td>
                <td className="k5-num text-muted-foreground">{entry.oldValue ?? '—'}</td>
                <td className="k5-num">{entry.newValue ?? '—'}</td>
                <td className="text-xs text-muted-foreground">
                  {entry.source === 'manual'
                    ? (entry.userName ?? entry.userEmail ?? 'Manual')
                    : describeSource(entry.source === 'integration' ? `integration:${entry.sourceDetail}` : entry.source)?.label}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Dialog>
  );
}
